import type { DataExport, Project, ProjectSummary, Task, TaskComment, TaskSummary } from "./entities";

type Schedulable = { scheduleKind: string };

export function normalizeLegacySchedule<T extends Schedulable>(value: T): T {
  return value.scheduleKind === "evening" ? { ...value, scheduleKind: "today" } : value;
}

export function normalizeLegacyExport(data: DataExport): { data: DataExport; migrated: number } {
  let migrated = 0;
  const normalize = <T extends Schedulable>(value: T) => {
    if (value.scheduleKind !== "evening") return value;
    migrated += 1;
    return normalizeLegacySchedule(value);
  };
  const normalizeProject = (project: Project): Project => {
    const normalized = normalize(project as Project & Schedulable) as Project;
    return normalized.status === "planned" || normalized.status === "completed" ? { ...normalized, status: "active" } : normalized;
  };
  const normalizeComment = (comment: TaskComment): TaskComment => {
    const value = comment as TaskComment & { authorType?: TaskComment["authorType"]; username?: string | null; role?: string | null };
    if (value.authorType === "user" || value.authorType === "agent") return comment;
    const role = value.role?.trim() || null;
    return { ...comment, authorType: role ? "agent" : "user", username: value.username ?? null, role };
  };
  return {
    data: {
      ...data,
      projects: data.projects.map(normalizeProject),
      tasks: data.tasks.map((task) => normalize(task as Task & Schedulable) as Task),
      comments: (data.comments ?? []).map(normalizeComment),
    },
    migrated,
  };
}

export function normalizeLegacyTaskSummary(task: TaskSummary): TaskSummary {
  return normalizeLegacySchedule({ ...task, tags: task.tags ?? [], checklist: task.checklist ?? [] } as TaskSummary & Schedulable) as TaskSummary;
}

export function normalizeLegacyProjectSummary(project: ProjectSummary): ProjectSummary {
  const progress = project.progress ?? { open: 0, completed: 0, cancelled: 0, total: 0, ratio: 0 };
  const completed = progress.completed ?? 0;
  const total = progress.total ?? 0;
  const normalizedProgress = {
    open: progress.open ?? Math.max(0, total - completed),
    completed,
    cancelled: progress.cancelled ?? 0,
    total,
    ratio: progress.ratio ?? (total ? completed / total : 0),
  };
  const attention = project.attention ?? (project.status === "archived"
    ? "archived"
    : project.status === "someday"
      ? "someday"
      : normalizedProgress.open > 0
        ? "active"
        : "quiet");
  return normalizeLegacySchedule({ ...project, progress: normalizedProgress, attention } as ProjectSummary & Schedulable) as ProjectSummary;
}
